import React from 'react'
import styled from 'styled-components'

import {Link as RouterLink} from 'react-router-dom'

import Button from '@material-ui/core/Button'
import Card from '@material-ui/core/Card'
import CardContent from '@material-ui/core/CardContent'
import CardActions from '@material-ui/core/CardActions'
import Typography from '@material-ui/core/Typography'
import TextField from '@material-ui/core/TextField'
import Accordion from '@material-ui/core/Accordion'
import AccordionSummary from '@material-ui/core/AccordionSummary'
import AccordionDetails from '@material-ui/core/AccordionDetails'
import MuiLink from '@material-ui/core/Link'

import ExpandMoreIcon from '@material-ui/icons/ExpandMore'
import HomeIcon from '@material-ui/icons/Home'
import ReloadIcon from '@material-ui/icons/Replay'

const ErrorMessageContainer = styled.div`
	width: 100%;
	display: flex;
	justify-content: center;
	padding: 12px;
`
const ErrorCard = styled(Card)`
	.MuiInputBase-root {
		font-family: monospace;
		color: rgba(0, 0, 0, 0.7);
	}
`

class ErrorBoundary extends React.Component {
	constructor(props) {
		super(props)
		this.state = {
			hasError: false,
			errorName: undefined,
			errorMessage: undefined,
			errorStack: undefined,
			componentStack: undefined,
		}
	}

	static getDerivedStateFromError(error) {
		return {
			hasError: true,
			errorName: error?.name || 'Error',
			errorMessage: error?.message || 'An unknown error occurred',
			errorStack: error?.stack || '',
		}
	}

	componentDidCatch(error, errorInfo) {
		// Log to console for debugging
		console.error('ErrorBoundary caught an error:', error)
		console.error('Error Info:', errorInfo)

		// Capture component stack for debugging
		this.setState({
			componentStack: errorInfo?.componentStack || '',
		})

		// Here you can also send error to Sentry or other error tracking service
		// Example: Sentry.captureException(error, { contexts: { react: { componentStack: errorInfo.componentStack } } })
	}

	render() {
		const {
			hasError,
			errorMessage,
			errorName,
			errorStack,
			componentStack,
		} = this.state

		if (hasError) {
			return (
				<ErrorMessageContainer>
					<ErrorCard variant="outlined">
						<CardContent>
							<div
								role="alert"
								aria-live="assertive"
								aria-atomic="true"
							>
								<Typography variant="h4">
									Something went wrong.
								</Typography>
								<Typography
									variant="body2"
									color="textSecondary"
									style={{marginTop: '8px'}}
								>
									{errorName}: {errorMessage}
								</Typography>
							</div>
							<CardActions>
								{/* TODO: attempt to add an intelligent back button here */}
								<Button
									variant="outlined"
									startIcon={<ReloadIcon />}
									color="primary"
									onClick={() => window.location.reload()}
								>
									Reload page
								</Button>
								<Button
									variant="outlined"
									startIcon={<HomeIcon />}
									color="primary"
									component={RouterLink}
									to="/"
								>
									Go to Homepage
								</Button>
							</CardActions>
							<Typography
								variant="subtitle1"
								style={{marginTop: '16px'}}
							>
								Please reach out to{' '}
								<MuiLink
									href="mailto:helpdesk@teachforindia.org"
									color="primary"
								>
									helpdesk@teachforindia.org
								</MuiLink>{' '}
								if this issue persists
							</Typography>
							<br />
							<Accordion elevation={0}>
								<AccordionSummary
									expandIcon={<ExpandMoreIcon />}
								>
									<Typography>
										Error details (for debugging)
									</Typography>
								</AccordionSummary>
								<AccordionDetails
									style={{flexDirection: 'column'}}
								>
									<div style={{width: '100%'}}>
										<Typography
											variant="caption"
											color="textSecondary"
										>
											<strong>Error Type:</strong>
										</Typography>
										<Typography
											variant="body2"
											style={{
												fontFamily: 'monospace',
												marginBottom: '12px',
												wordBreak: 'break-word',
											}}
										>
											{errorName}
										</Typography>

										<Typography
											variant="caption"
											color="textSecondary"
										>
											<strong>Error Message:</strong>
										</Typography>
										<Typography
											variant="body2"
											style={{
												fontFamily: 'monospace',
												marginBottom: '12px',
												wordBreak: 'break-word',
											}}
										>
											{errorMessage}
										</Typography>

										{errorStack && (
											<>
												<Typography
													variant="caption"
													color="textSecondary"
												>
													<strong>
														Stack Trace:
													</strong>
												</Typography>
												<TextField
													value={errorStack}
													InputProps={{
														readOnly: true,
													}}
													fullWidth
													multiline
													minRows={6}
													size="small"
													style={{
														fontFamily: 'monospace',
														marginBottom: '12px',
													}}
												/>
											</>
										)}

										{componentStack && (
											<>
												<Typography
													variant="caption"
													color="textSecondary"
												>
													<strong>
														Component Stack:
													</strong>
												</Typography>
												<TextField
													value={componentStack}
													InputProps={{
														readOnly: true,
													}}
													fullWidth
													multiline
													minRows={6}
													size="small"
													style={{
														fontFamily: 'monospace',
													}}
												/>
											</>
										)}
									</div>
								</AccordionDetails>
							</Accordion>
						</CardContent>
					</ErrorCard>
				</ErrorMessageContainer>
			)
		} else return this.props.children
	}
}

export default ErrorBoundary
